import React, { useState, useRef, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calculator, Package, MapPin, Truck, Clock, Info, Download, ChevronDown, Scale, ArrowRight, HelpCircle, CheckCircle, Navigation, X } from 'lucide-react';
import { Button } from './ui/Button';
type PackageType = 'documents' | 'electronics' | 'clothing' | 'food' | 'other' | '';
type WeightUnit = 'lbs' | 'kg';
interface CalculatorData {
  origin: string;
  packageType: PackageType;
  weight: string;
  weightUnit: WeightUnit;
  destination: 'lagos' | 'lagos_plus_state';
  destinationState: string;
}
const INITIAL_DATA: CalculatorData = {
  origin: '',
  packageType: '',
  weight: '',
  weightUnit: 'lbs',
  destination: 'lagos',
  destinationState: ''
};
const BASE_RATES: Record<string, number> = {
  documents: 25,
  electronics: 45,
  clothing: 35,
  food: 40,
  other: 30
};
const PACKAGE_LABELS: Record<string, string> = {
  documents: 'Documents & Papers',
  electronics: 'Electronics & Gadgets',
  clothing: 'Clothing & Textiles',
  food: 'Food Items',
  other: 'Other Items'
};
const NIGERIAN_STATES = [{
  name: 'Abuja (FCT)',
  fee: 35
}, {
  name: 'Rivers',
  fee: 40
}, {
  name: 'Kano',
  fee: 45
}, {
  name: 'Kaduna',
  fee: 40
}, {
  name: 'Oyo',
  fee: 30
}, {
  name: 'Enugu',
  fee: 35
}, {
  name: 'Delta',
  fee: 38
}, {
  name: 'Anambra',
  fee: 35
}, {
  name: 'Ogun',
  fee: 25
}, {
  name: 'Edo',
  fee: 35
}];
const DROP_OFF_LOCATIONS = [{
  id: 'ny',
  name: 'New York, NY',
  address: '123 Main St, New York, NY 10001'
}, {
  id: 'la',
  name: 'Los Angeles, CA',
  address: '456 Oak Ave, Los Angeles, CA 90001'
}, {
  id: 'houston',
  name: 'Houston, TX',
  address: '789 Pine Rd, Houston, TX 77001'
}, {
  id: 'atlanta',
  name: 'Atlanta, GA',
  address: '321 Peach St, Atlanta, GA 30301'
}];
interface ShippingCalculatorProps {
  onStartShipping?: () => void;
  compact?: boolean;
}
export function ShippingCalculator({
  onStartShipping,
  compact = false
}: ShippingCalculatorProps) {
  const [data, setData] = useState<CalculatorData>(INITIAL_DATA);
  const [isCalculated, setIsCalculated] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const quoteRef = useRef<HTMLDivElement>(null);
  const [costs, setCosts] = useState({
    base: 0,
    weight: 0,
    interstate: 0,
    total: 0
  });
  // Calculate costs
  const calculateCosts = () => {
    const baseRate = data.packageType ? BASE_RATES[data.packageType] : 0;
    let weightInLbs = parseFloat(data.weight) || 0;
    if (data.weightUnit === 'kg') {
      weightInLbs = weightInLbs * 2.20462;
    }
    const weightCost = weightInLbs * 2.5;
    let interstateCost = 0;
    if (data.destination === 'lagos_plus_state' && data.destinationState) {
      const state = NIGERIAN_STATES.find(s => s.name === data.destinationState);
      interstateCost = state?.fee || 30;
    }
    const total = baseRate + weightCost + interstateCost;
    setCosts({
      base: baseRate,
      weight: weightCost,
      interstate: interstateCost,
      total
    });
    setIsCalculated(true);
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setData(prev => ({
      ...prev,
      [name]: value
    }));
    setIsCalculated(false);
  };
  const handleUnitToggle = (unit: WeightUnit) => {
    setData(prev => ({
      ...prev,
      weightUnit: unit
    }));
    setIsCalculated(false);
  };
  const handlePrint = () => {
    window.print();
  };
  const handleExport = () => {
    const quoteData = {
      date: new Date().toLocaleDateString(),
      origin: DROP_OFF_LOCATIONS.find(o => o.id === data.origin)?.name || 'N/A',
      packageType: PACKAGE_LABELS[data.packageType],
      weight: `${data.weight} ${data.weightUnit}`,
      destination: data.destination === 'lagos' ? 'Lagos, Nigeria' : `Lagos + ${data.destinationState}`,
      breakdown: {
        baseRate: costs.base,
        weightCharge: costs.weight,
        interstateDelivery: costs.interstate
      },
      total: costs.total,
      estimatedDelivery: '7-14 business days'
    };
    const blob = new Blob([JSON.stringify(quoteData, null, 2)], {
      type: 'application/json'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `taskrunner-quote-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const handleCancel = () => {
    setData(INITIAL_DATA);
    setCosts({
      base: 0,
      weight: 0,
      interstate: 0,
      total: 0
    });
    setIsCalculated(false);
    setShowBreakdown(false);
  };
  const isFormValid = data.origin && data.packageType && data.weight && parseFloat(data.weight) > 0 && (data.destination === 'lagos' || data.destination === 'lagos_plus_state' && data.destinationState);
  return <div className={`${compact ? '' : 'py-12'}`} data-id="element-765">
      <div className={`${compact ? '' : 'max-w-4xl mx-auto px-4'}`} data-id="element-766">
        {!compact && <div className="text-center mb-10" data-id="element-767">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-50 text-brand-600 text-sm font-medium mb-4" data-id="element-768">
              <Calculator className="w-4 h-4" data-id="element-769" />
              Instant Quote
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4" data-id="element-770">
              Shipping Cost Calculator
            </h1>
            <p className="text-gray-600 max-w-xl mx-auto" data-id="element-771">
              Get an instant estimate for your shipment from USA to Nigeria. No
              hidden fees, transparent pricing.
            </p>
          </div>}

        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden" ref={quoteRef} data-id="element-772">
          {/* Calculator Header */}
          <div className="bg-gradient-to-r from-brand-600 to-brand-700 p-6 text-white" data-id="element-773">
            <div className="flex items-center justify-between" data-id="element-774">
              <div className="flex items-center gap-3" data-id="element-775">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center" data-id="element-776">
                  <Package className="w-6 h-6 text-white" data-id="element-777" />
                </div>
                <div data-id="element-778">
                  <h2 className="font-bold text-lg text-white" data-id="element-779">
                    Estimated Cost
                  </h2>
                  <p className="text-white/80 text-sm" data-id="element-780">USA → Lagos, Nigeria</p>
                </div>
              </div>
              <div className="text-right" data-id="element-781">
                <AnimatePresence mode="wait" data-id="element-782">
                  {isCalculated ? <motion.div key="price" initial={{
                  opacity: 0,
                  scale: 0.8
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  scale: 0.8
                }} data-id="element-783">
                      <span className="text-4xl font-bold text-white" data-id="element-784">
                        ${costs.total.toFixed(2)}
                      </span>
                      <span className="text-white/80 text-sm block" data-id="element-785">USD</span>
                    </motion.div> : <motion.div key="placeholder" initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} exit={{
                  opacity: 0
                }} data-id="element-786">
                      <span className="text-2xl text-white/90" data-id="element-787">$---.--</span>
                      <span className="text-white/80 text-sm block" data-id="element-788">
                        Enter details below
                      </span>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </div>
          </div>

          {/* Calculator Form */}
          <div className="p-6 space-y-6" data-id="element-789">
            {/* Origin */}
            <div className="space-y-2" data-id="element-790">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700" data-id="element-791">
                <Navigation className="w-4 h-4 text-brand-500" data-id="element-792" />
                Drop-off Location (USA)
              </label>
              <p className="text-xs text-gray-500" data-id="element-793">
                Select the TaskRunner location where you'll drop off your
                package
              </p>
              <select name="origin" value={data.origin} onChange={handleInputChange} className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand focus:border-brand outline-none bg-white text-gray-900" data-id="element-794">
                <option value="" className="text-gray-500" data-id="element-795">
                  Select drop-off location...
                </option>
                {DROP_OFF_LOCATIONS.map(location => <option key={location.id} value={location.id} className="text-gray-900" data-id="element-796">
                    {location.name}
                  </option>)}
              </select>
            </div>

            {/* Package Type & Weight Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-797">
              <div className="space-y-2" data-id="element-798">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700" data-id="element-799">
                  <Package className="w-4 h-4 text-brand-500" data-id="element-800" />
                  Package Type
                </label>
                <select name="packageType" value={data.packageType} onChange={handleInputChange} className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand focus:border-brand outline-none bg-white text-gray-900" data-id="element-801">
                  <option value="" disabled className="text-gray-500" data-id="element-802">
                    Select package type...
                  </option>
                  {Object.entries(PACKAGE_LABELS).map(([key, label]) => <option key={key} value={key} className="text-gray-900" data-id="element-803">
                      {label}
                    </option>)}
                </select>
              </div>

              <div className="space-y-2" data-id="element-804">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700" data-id="element-805">
                  <Scale className="w-4 h-4 text-brand-500" data-id="element-806" />
                  Package Weight
                </label>
                <div className="relative flex rounded-xl border border-gray-300 focus-within:ring-2 focus-within:ring-brand focus-within:border-brand overflow-hidden bg-white" data-id="element-807">
                  <input type="number" name="weight" value={data.weight} onChange={handleInputChange} className="w-full px-4 py-3 pr-24 outline-none bg-white text-gray-900 placeholder-gray-400" placeholder="Enter weight..." min="0" step="0.1" data-id="element-808" />
                  <div className="absolute right-0 top-0 bottom-0 flex" data-id="element-809">
                    <button type="button" onClick={() => handleUnitToggle('lbs')} className={`px-3 py-3 text-sm font-semibold transition-colors ${data.weightUnit === 'lbs' ? 'bg-brand text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-810">
                      lbs
                    </button>
                    <button type="button" onClick={() => handleUnitToggle('kg')} className={`px-3 py-3 text-sm font-semibold transition-colors ${data.weightUnit === 'kg' ? 'bg-brand text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-811">
                      kg
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Destination */}
            <div className="space-y-4" data-id="element-812">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700" data-id="element-813">
                <MapPin className="w-4 h-4 text-brand-500" data-id="element-814" />
                Destination in Nigeria
              </label>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-815">
                <label className={`relative flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${data.destination === 'lagos' ? 'border-brand bg-brand-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-816">
                  <input type="radio" name="destination" value="lagos" checked={data.destination === 'lagos'} onChange={handleInputChange} className="sr-only" data-id="element-817" />
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${data.destination === 'lagos' ? 'border-brand' : 'border-gray-300'}`} data-id="element-818">
                    {data.destination === 'lagos' && <div className="w-3 h-3 rounded-full bg-brand" data-id="element-819" />}
                  </div>
                  <div data-id="element-820">
                    <span className="font-medium text-gray-900" data-id="element-821">
                      Lagos Only
                    </span>
                    <span className="text-sm text-gray-500 block" data-id="element-822">
                      Delivery within Lagos
                    </span>
                  </div>
                </label>

                <label className={`relative flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${data.destination === 'lagos_plus_state' ? 'border-brand bg-brand-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-823">
                  <input type="radio" name="destination" value="lagos_plus_state" checked={data.destination === 'lagos_plus_state'} onChange={handleInputChange} className="sr-only" data-id="element-824" />
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${data.destination === 'lagos_plus_state' ? 'border-brand' : 'border-gray-300'}`} data-id="element-825">
                    {data.destination === 'lagos_plus_state' && <div className="w-3 h-3 rounded-full bg-brand" data-id="element-826" />}
                  </div>
                  <div data-id="element-827">
                    <span className="font-medium text-gray-900" data-id="element-828">
                      Lagos + Another State
                    </span>
                    <span className="text-sm text-gray-500 block" data-id="element-829">
                      + Interstate delivery
                    </span>
                  </div>
                </label>
              </div>

              <AnimatePresence data-id="element-830">
                {data.destination === 'lagos_plus_state' && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} className="overflow-hidden" data-id="element-831">
                    <select name="destinationState" value={data.destinationState} onChange={handleInputChange} className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand focus:border-brand outline-none bg-white text-gray-900" data-id="element-832">
                      <option value="" className="text-gray-500" data-id="element-833">
                        Select destination state...
                      </option>
                      {NIGERIAN_STATES.map(state => <option key={state.name} value={state.name} className="text-gray-900" data-id="element-834">
                          {state.name} (+${state.fee})
                        </option>)}
                    </select>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Calculate Button */}
            <Button fullWidth size="lg" onClick={calculateCosts} disabled={!isFormValid} className="gap-2" data-id="element-835">
              <Calculator className="w-5 h-5" data-id="element-836" />
              Calculate Price
            </Button>
          </div>

          {/* Results Section */}
          <AnimatePresence data-id="element-837">
            {isCalculated && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} className="border-t border-gray-200" data-id="element-838">
                <div className="p-6 bg-gray-50 space-y-6" data-id="element-839">
                  {/* Delivery Time */}
                  <div className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-200" data-id="element-840">
                    <div className="w-10 h-10 bg-brand-50 rounded-lg flex items-center justify-center" data-id="element-841">
                      <Clock className="w-5 h-5 text-brand-600" data-id="element-842" />
                    </div>
                    <div data-id="element-843">
                      <span className="text-sm text-gray-500" data-id="element-844">
                        Estimated Delivery
                      </span>
                      <span className="font-bold text-gray-900 block" data-id="element-845">
                        7-14 Business Days
                      </span>
                    </div>
                  </div>

                  {/* Cost Breakdown */}
                  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-846">
                    <button onClick={() => setShowBreakdown(!showBreakdown)} className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors" data-id="element-847">
                      <span className="flex items-center gap-2 font-medium text-gray-900" data-id="element-848">
                        <Info className="w-4 h-4 text-brand-500" data-id="element-849" />
                        Cost Breakdown
                      </span>
                      <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showBreakdown ? 'rotate-180' : ''}`} data-id="element-850" />
                    </button>

                    <AnimatePresence data-id="element-851">
                      {showBreakdown && <motion.div initial={{
                    height: 0
                  }} animate={{
                    height: 'auto'
                  }} exit={{
                    height: 0
                  }} className="overflow-hidden" data-id="element-852">
                          <div className="px-4 pb-4 space-y-3 border-t border-gray-100 pt-4" data-id="element-853">
                            <div className="flex justify-between text-sm" data-id="element-854">
                              <span className="text-gray-600 flex items-center gap-2" data-id="element-855">
                                <div className="w-4 h-4" data-id="element-856" />
                                Base Rate ({PACKAGE_LABELS[data.packageType]})
                              </span>
                              <span className="font-medium" data-id="element-857">
                                ${costs.base.toFixed(2)}
                              </span>
                            </div>
                            <div className="flex justify-between text-sm" data-id="element-858">
                              <span className="text-gray-600 flex items-center gap-2" data-id="element-859">
                                <Scale className="w-4 h-4" data-id="element-860" />
                                Weight Charge ({data.weight} {data.weightUnit} ×
                                $2.50/lb)
                              </span>
                              <span className="font-medium" data-id="element-861">
                                ${costs.weight.toFixed(2)}
                              </span>
                            </div>
                            {costs.interstate > 0 && <div className="flex justify-between text-sm" data-id="element-862">
                                <span className="text-gray-600 flex items-center gap-2" data-id="element-863">
                                  <Truck className="w-4 h-4" data-id="element-864" />
                                  Interstate Delivery ({data.destinationState})
                                </span>
                                <span className="font-medium" data-id="element-865">
                                  ${costs.interstate.toFixed(2)}
                                </span>
                              </div>}
                            <div className="pt-3 border-t border-gray-100 flex justify-between" data-id="element-866">
                              <span className="font-bold text-gray-900" data-id="element-867">
                                Total
                              </span>
                              <span className="font-bold text-brand text-lg" data-id="element-868">
                                ${costs.total.toFixed(2)}
                              </span>
                            </div>
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>

                  {/* Tooltip Info */}
                  <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-xl border border-blue-100" data-id="element-869">
                    <HelpCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" data-id="element-870" />
                    <div className="text-sm text-blue-700" data-id="element-871">
                      <p className="font-medium mb-1" data-id="element-872">
                        How is the price calculated?
                      </p>
                      <ul className="space-y-1 text-blue-600" data-id="element-873">
                        <li data-id="element-874">• Base rate varies by package type ($25-$45)</li>
                        <li data-id="element-875">• Weight charged at $2.50 per lb ($5.50 per kg)</li>
                        <li data-id="element-876">
                          • Interstate delivery adds $25-$45 depending on state
                        </li>
                      </ul>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3" data-id="element-877">
                    <Button fullWidth onClick={onStartShipping} className="gap-2" data-id="element-878">
                      Start Shipping Now
                      <ArrowRight className="w-4 h-4" data-id="element-879" />
                    </Button>
                    <div className="flex gap-3 sm:flex-shrink-0" data-id="element-880">
                      <Button variant="outline" onClick={handleCancel} className="flex-1 sm:flex-initial gap-2" data-id="element-881">
                        <X className="w-4 h-4" data-id="element-882" />
                        Cancel
                      </Button>
                      <Button variant="outline" onClick={handleExport} className="flex-1 sm:flex-initial gap-2" data-id="element-883">
                        <Download className="w-4 h-4" data-id="element-884" />
                        Export
                      </Button>
                    </div>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>

        {/* Features */}
        {!compact && <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6" data-id="element-885">
            <div className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-200" data-id="element-886">
              <CheckCircle className="w-6 h-6 text-success" data-id="element-887" />
              <div data-id="element-888">
                <span className="font-medium text-gray-900 block" data-id="element-889">
                  No Hidden Fees
                </span>
                <span className="text-sm text-gray-500" data-id="element-890">
                  Transparent pricing
                </span>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-200" data-id="element-891">
              <CheckCircle className="w-6 h-6 text-success" data-id="element-892" />
              <div data-id="element-893">
                <span className="font-medium text-gray-900 block" data-id="element-894">
                  Instant Quotes
                </span>
                <span className="text-sm text-gray-500" data-id="element-895">
                  Real-time calculation
                </span>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-200" data-id="element-896">
              <CheckCircle className="w-6 h-6 text-success" data-id="element-897" />
              <div data-id="element-898">
                <span className="font-medium text-gray-900 block" data-id="element-899">
                  Best Rates
                </span>
                <span className="text-sm text-gray-500" data-id="element-900">
                  Competitive pricing
                </span>
              </div>
            </div>
          </div>}
      </div>
    </div>;
}